import {
  ApiError,
  fetchDetail,
  fetchListing,
  fetchProviders,
  fetchStream,
  searchAll,
  type ProviderSummary,
  type VideoDetail,
  type VideoSummary,
} from "./api.ts";
import { categoryTitle } from "./categories.ts";
import {
  displayTitle,
  emptyState,
  errorState,
  favoriteButton,
  indexRow,
  posterCard,
  providerRail,
  rail,
  resolveImageUrl,
  skeletonGrid,
  skeletonRail,
} from "./components.ts";
import { el, formatClock, logoImage, showAlert, showToast } from "./dom.ts";
import { icons } from "./icons.ts";
import { openPlayer } from "./player.ts";
import { providerSearchView } from "./search-views.ts";
import { visibleProviders } from "./visibility.ts";

export { settingsView } from "./settings-view.ts";
import {
  acceptGate,
  addToPlaylist,
  continueWatching,
  createPlaylist,
  favoriteList,
  feedCategoryIds,
  feedProviders,
  feedRowSites,
  getState,
  recentHistory,
  toPlaybackRecord,
  toggleFavorite,
} from "./store.ts";

export interface AppContext {
  providers: ProviderSummary[];
  navigate: (path: string) => void;
  /** Providers verified through the deployed worker, best first. */
  preferred: string[];
}

function providerName(context: AppContext, id: string): string {
  return context.providers.find((provider) => provider.id === id)?.name ?? id;
}

let playQueue: VideoSummary[] = [];

/** Cards open the title page first; playback starts from its Play button. */
function openDetail(context: AppContext, video: VideoSummary): void {
  context.navigate(`#/watch/${encodeURIComponent(video.provider)}/${encodeURIComponent(video.id)}`);
}


/**
 * A stream that cannot be resolved should not be a dead end. The title lives on
 * its own site, so the page says what happened and offers the way there.
 */
function playbackFallback(
  context: AppContext,
  video: VideoSummary,
  message: string,
  retry: () => void,
  embeds: string[] = [],
): void {
  const host = document.querySelector<HTMLElement>(".watch-body");
  if (!host) {
    showAlert(message);
    return;
  }
  host.querySelector(".playback-fallback")?.remove();
  const provider = context.providers.find((entry) => entry.id === video.provider);
  const base = provider?.siteUrl?.replace(/\/$/, "");
  const target = video.url ?? (base ? `${base}/${encodeURIComponent(video.id)}/` : "");
  const again = el("button", { class: "btn btn-secondary", attrs: { type: "button" }, text: "Try again" });
  again.addEventListener("click", () => {
    host.querySelector(".playback-fallback")?.remove();
    retry();
  });
  // The page's own players, offered one by one: often more direct than the post
  // page, and the only thing left when a host refuses to be framed.
  const hosts = [...new Set(
    embeds
      .map((url) => {
        try {
          return new URL(url);
        } catch {
          return null;
        }
      })
      .filter((parsed): parsed is URL => Boolean(parsed))
      .map((parsed) => parsed.hostname.replace(/^www\./, "")),
  )];
  const playerLinks = embeds.map((url, index) =>
    el("a", {
      class: index === 0 ? "btn btn-primary" : "btn btn-secondary",
      attrs: { href: url, target: "_blank", rel: "noopener noreferrer" },
      text: `Open ${hosts[index] ?? "player"}`,
    }),
  );

  const panel = el(
    "div",
    { class: "playback-fallback", attrs: { role: "status" } },
    el("p", { class: "playback-fallback-text", text: message }),
    el("p", {
      class: "playback-fallback-hint",
      text:
        playerLinks.length > 0
          ? `${provider?.name ?? video.provider} plays this from ${playerLinks.length === 1 ? "a player" : `${playerLinks.length} players`} this site cannot open. Open one directly:`
          : `${provider?.name ?? video.provider} plays this from its own player, which this site cannot open.`,
    }),
    el(
      "div",
      { class: "playback-fallback-actions" },
      ...playerLinks,
      target && playerLinks.length === 0
        ? el("a", {
            class: "btn btn-primary",
            attrs: { href: target, target: "_blank", rel: "noopener noreferrer" },
            text: `Open on ${provider?.name ?? "the site"}`,
          })
        : null,
      playerLinks.length > 0 && target
        ? el("a", {
            class: "btn btn-ghost",
            attrs: { href: target, target: "_blank", rel: "noopener noreferrer" },
            text: `Open the ${provider?.name ?? "site"} page`,
          })
        : null,
      again,
    ),
  );
  host.prepend(panel);
}

export function openVideo(context: AppContext, video: VideoSummary, startAt = 0): void {
  let index = playQueue.findIndex(
    (entry) => entry.id === video.id && entry.provider === video.provider,
  );
  if (index < 0) {
    playQueue = [video];
    index = 0;
  }
  const queue = {
    position: index,
    length: playQueue.length,
    onNext: () => {
      const next = playQueue[index + 1];
      if (next) openVideo(context, next);
    },
    onPrevious: () => {
      const previous = playQueue[index - 1];
      if (previous) openVideo(context, previous);
    },
  };
  void (async () => {
    try {
      const media = await fetchStream(video.provider, video.id);
      if (media.length === 0) {
        playbackFallback(context, video, "No playable stream was found for this title.", () =>
          openVideo(context, video, startAt),
        );
        return;
      }
      const detail = await fetchDetail(video.provider, video.id).catch(() => null);
      openPlayer({
        media,
        title: video.title,
        subtitle: providerName(context, video.provider),
        backdrop: video.thumbnail,
        provider: video.provider,
        videoId: video.id,
        startAt: startAt > 0 ? startAt : undefined,
        queue,
        onEnded: () => {
          if (!getState().settings.autoplay) return;
          const next = playQueue[index + 1];
          if (next) openVideo(context, next);
        },
      });
      if (detail) document.title = `${video.title} · AIOX`;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load this stream";
      const embeds = error instanceof ApiError ? error.embeds : [];
      playbackFallback(context, video, message, () => openVideo(context, video, startAt), embeds);
    }
  })();
}

/** A section: a head (title, meta, action) and one composition under it. */
function section(
  title: string,
  body: HTMLElement,
  options: { meta?: string; action?: { label: string; onOpen: () => void }; id?: string } = {},
): HTMLElement {
  const head = el(
    "div",
    { class: "section-head" },
    el("h2", { class: "section-title", text: title }),
    options.meta ? el("span", { class: "section-meta", text: options.meta }) : null,
  );
  if (options.action) {
    const button = el("button", { class: "ghost-link", attrs: { type: "button" }, text: options.action.label });
    button.addEventListener("click", () => options.action?.onOpen());
    head.append(el("div", { class: "section-actions" }, button));
  }
  return el("section", { class: "section", attrs: options.id ? { id: options.id } : {} }, head, body);
}

function posterGrid(
  items: VideoSummary[],
  context: AppContext,
  options: { providerLabel?: string } = {},
): HTMLElement {
  const grid = el("ul", { class: "grid" });
  for (const item of items) {
    grid.append(
      posterCard(item, {
        providerLabel: options.providerLabel ?? providerName(context, item.provider),
        onOpen: (video) => openDetail(context, video),
      }),
    );
  }
  return grid;
}

interface HeroSlide {
  tag: string;
  title: string;
  meta: string;
  art?: string;
  primary: { label: string; run: () => void };
  secondary: { label: string; run: () => void };
}

export function homeView(context: AppContext): HTMLElement {
  const root = el("div", { class: "view view--home" });
  const preferred = new Set(context.preferred);
  const working = visibleProviders(context.providers)
    .filter((provider) => provider.state !== "offline")
    .sort(
      (left, right) =>
        Number(preferred.has(right.id)) - Number(preferred.has(left.id)) ||
        left.name.localeCompare(right.name),
    );
  const byId = new Map(working.map((provider) => [provider.id, provider]));
  // An explicit selection is the page's whole source of truth, in the order it
  // was picked. No selection means every working provider, preferred first.
  const chosen = feedProviders()
    .map((id) => byId.get(id))
    .filter((provider): provider is ProviderSummary => Boolean(provider));
  const pool = chosen.length > 0 ? chosen : working;
  const categories = feedCategoryIds([...new Set(working.map((provider) => provider.category))]);
  const online = working.length;
  const providerNames = Object.fromEntries(
    context.providers.map((provider) => [provider.id, provider.name]),
  );

  /** Fetch one provider's page, never letting a slow site hold the whole page. */
  let pendingFetches = 0;
  const listing = async (
    provider: ProviderSummary,
    sort: "new" | "popular",
    limit: number,
  ): Promise<VideoSummary[]> => {
    pendingFetches += 1;
    try {
      const items = await fetchListing(provider.id, { sort, limit });
      // Stamp the provider so a card knows where it came from even when the
      // provider engine omits it.
      return items.map((item) => ({ ...item, provider: provider.id }));
    } catch {
      return [];
    } finally {
      pendingFetches -= 1;
      // Rows paint in the continuation after this resolves, so wait a task
      // before deciding the page is genuinely empty.
      if (pendingFetches === 0) setTimeout(showFeedError, 0);
    }
  };

  const band = (body: HTMLElement): HTMLElement =>
    el("section", { class: "band" }, el("div", { class: "container" }, body));

  // ---- header + spotlight ---------------------------------------------
  // Tube catalogues open on content, not on a billboard: xHamster leads with a
  // filter row and the grid, SpankBang with quality, duration and tabs. The
  // header stays one line of type, and the first screen is titles.
  const header = el(
    "header",
    { class: "container home-head" },
    el("h1", { class: "home-title", text: "Every studio. One screen." }),
    el("p", {
      class: "home-sub",
      text: `${online} providers in one catalogue. One search, one player.`,
    }),
    el(
      "div",
      { class: "home-actions" },
      (() => {
        const browse = el("button", { class: "btn btn-primary", attrs: { type: "button" }, text: "Browse providers" });
        browse.addEventListener("click", () => context.navigate("#/providers"));
        return browse;
      })(),
      (() => {
        const search = el("button", { class: "btn btn-ghost", attrs: { type: "button" }, text: "Search the catalogue" });
        search.addEventListener("click", () => context.navigate("#/search"));
        return search;
      })(),
      (() => {
        const customize = el("button", {
          class: "btn btn-ghost home-customize",
          attrs: { type: "button" },
          html: `${icons.grid}<span>Customize home</span>`,
        });
        customize.addEventListener("click", () => context.navigate("#/customize"));
        return customize;
      })(),
    ),
  );
  root.append(header);

  // ---- providers ------------------------------------------------------
  // The catalogue's spine: the sites themselves come before any of their
  // titles, so a reader can start from a studio they already trust.
  root.append(
    band(
      providerRail(pool, {
        title: "Browse by provider",
        meta: `${pool.length} ${pool.length === 1 ? "site" : "sites"}`,
        onOpen: (id) => context.navigate(`#/provider/${id}`),
      }),
    ),
  );

  const spotlight = el("section", { class: "band band--spotlight", attrs: { "aria-labelledby": "spotlight-title" } });
  const spotlightGrid = el("ul", { class: "spotlight-grid" });
  spotlight.append(
    el(
      "div",
      { class: "container" },
      el(
        "div",
        { class: "section-head" },
        el("h2", { class: "section-title", attrs: { id: "spotlight-title" }, text: "Spotlight" }),
        el("span", { class: "section-meta", text: "Most viewed right now" }),
      ),
      spotlightGrid,
    ),
  );
  root.append(spotlight);

  const paintSpotlight = (picks: VideoSummary[]): void => {
    spotlightGrid.replaceChildren(
      ...picks.map((video) =>
        posterCard(video, {
          providerLabel: providerName(context, video.provider),
          onOpen: (item) => openDetail(context, item),
        }),
      ),
    );
    spotlight.hidden =
      spotlightGrid.childElementCount === 0 || !getState().settings.spotlightEnabled;
  };
  spotlight.hidden = true;

  void (async () => {
    if (!getState().settings.spotlightEnabled) return;
    // Six titles from six sites. Ask in small waves so a dead provider is
    // replaced by the next one instead of leaving the band short.
    const picks: VideoSummary[] = [];
    const seen = new Set<string>();
    for (let cursor = 0; cursor < pool.length && picks.length < 6; cursor += 8) {
      const answers = await Promise.all(
        pool
          .slice(cursor, cursor + 8)
          .map(async (provider) => ({ provider, items: await listing(provider, "popular", 4) })),
      );
      for (const { provider, items } of answers) {
        if (picks.length >= 6) break;
        if (seen.has(provider.id)) continue;
        const item = items.find((entry) => Boolean(entry.image ?? entry.thumbnail));
        if (!item) continue;
        seen.add(provider.id);
        picks.push(item);
      }
    }
    paintSpotlight(picks);
  })();

  let feedErrorShown = false;
  let feedErrorAttempts = 0;
  const showFeedError = (): void => {
    if (feedErrorShown) return;
    // Provider tiles come from the local catalogue and render offline; only
    // fetched artwork proves the network answered.
    if (root.querySelector(".poster-cell, .detail-art img")) return;
    if (root.querySelector("[class*=skeleton]")) {
      if (feedErrorAttempts++ < 20) setTimeout(showFeedError, 500);
      return;
    }
    feedErrorShown = true;
    const retry = el("button", { class: "btn btn-ghost", attrs: { type: "button" }, text: "Try again" });
    retry.addEventListener("click", () => {
      // Re-entering the route rebuilds the view and retries every band.
      location.hash = "#/home";
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    });
    root.append(
      el(
        "section",
        { class: "band" },
        el(
          "div",
          { class: "container" },
          el(
            "p",
            { class: "empty-body", attrs: { role: "alert" } },
            el("strong", { text: "Nothing loaded. " }),
            el("span", { text: "The catalogue did not answer, so this page has no titles to show." }),
            el("br"),
            retry,
          ),
        ),
      ),
    );
  };

  // ---- continue watching ----------------------------------------------
  const watched = continueWatching();
  if (watched.length > 0) {
    const progress: Record<string, number> = {};
    const items = watched.map((entry) => {
      if (entry.duration > 0) progress[entry.videoId] = Math.min(1, entry.position / entry.duration);
      return {
        id: entry.videoId,
        title: entry.title,
        thumbnail: entry.thumbnail,
        provider: entry.provider,
        duration: entry.duration > 0 ? formatDurationShort(entry.duration) : undefined,
      };
    });
    root.append(
      band(
        rail({
          title: "Continue watching",
          progress,
          items,
          providerNames,
          viewAll: { onOpen: () => context.navigate("#/library") },
          onOpen: (video) => openDetail(context, video),
        }),
      ),
    );
  }

  // ---- provider and category rows -------------------------------------
  // Every row is one provider's page (or two, for a category), fetched with a
  // real limit. A row under three titles is a gap, so the next provider takes
  // its place instead of the row staying empty.
  const rowsStack = el("div", { class: "rail-stack" }, skeletonRail(4));
  const rowsBand = band(rowsStack);
  root.append(rowsBand);

  const paintedProviders = new Set<string>();
  // One title appears once on the page: a provider that lists the same video
  // twice used to show it in two rows as well.
  const seenOnPage = new Set<string>();
  const freshForPage = (items: VideoSummary[]): VideoSummary[] => {
    const out: VideoSummary[] = [];
    for (const item of items) {
      const key = `${item.provider}:${item.id}`;
      if (seenOnPage.has(key)) continue;
      seenOnPage.add(key);
      out.push(item);
    }
    return out;
  };
  let rowCount = 0;
  const rowChoices = feedRowSites();
  const sitesForRow = (category: string, providers: ProviderSummary[]): ProviderSummary[] => {
    const stored = rowChoices[category];
    if (!stored || stored.length === 0) return providers;
    const keep = new Set(stored);
    const picked = providers.filter((provider) => keep.has(provider.id));
    return picked.length > 0 ? picked : providers;
  };
  const paintRow = (title: string, meta: string, items: VideoSummary[], onAll: () => void): void => {
    const fresh = freshForPage(items);
    if (fresh.length === 0) return;
    rowsStack.append(
      rail({
        title,
        meta,
        items: fresh,
        providerNames,
        viewAll: { onOpen: onAll },
        onOpen: (video) => openDetail(context, video),
      }),
    );
    rowCount += 1;
  };

  const paintProviderRows = async (): Promise<void> => {
    const candidates = pool.slice(0, 16);
    let cursor = 0;
    let painted = 0;
    while (painted < 8 && cursor < candidates.length) {
      const batch = candidates.slice(cursor, cursor + 6);
      cursor += batch.length;
      const answers = await Promise.all(
        batch.map(async (provider) => ({
          provider,
          items: await listing(provider, "popular", 18),
        })),
      );
      for (const { provider, items } of answers) {
        if (painted >= 8) break;
        if (items.length < 3) continue;
        if (painted === 0) rowsStack.replaceChildren();
        paintRow(provider.name, categoryTitle(provider.category), items, () =>
          context.navigate(`#/provider/${provider.id}`),
        );
        painted += 1;
        paintedProviders.add(provider.id);
      }
    }
  };

  const paintCategoryRows = async (): Promise<void> => {
    let painted = 0;
    for (const category of categories) {
      if (painted >= 3) break;
      // Unused sites first, so three category rows widen the page rather than
      // repeating the provider rows above them.
      const candidates = sitesForRow(
        category,
        pool.filter((provider) => provider.category === category),
      )
        .sort(
          (left, right) =>
            Number(paintedProviders.has(left.id)) - Number(paintedProviders.has(right.id)),
        );
      let picked: ProviderSummary[] = [];
      let items: VideoSummary[] = [];
      for (let cursor = 0; cursor < candidates.length && cursor < 6; cursor += 2) {
        const answers = await Promise.all(
          candidates.slice(cursor, cursor + 2).map(async (provider) => ({
            provider,
            items: await listing(provider, "new", 14),
          })),
        );
        const merged = answers.flatMap((answer) => answer.items).slice(0, 18);
        if (merged.length < 3) continue;
        picked = answers
          .filter((answer) => answer.items.length > 0)
          .map((answer) => answer.provider);
        items = merged;
        break;
      }
      if (items.length < 3) continue;
      if (painted === 0 && rowCount === 0) rowsStack.replaceChildren();
      paintRow(categoryTitle(category), picked.map((provider) => provider.name).join(", "), items, () =>
        context.navigate("#/categories"),
      );
      for (const provider of picked) paintedProviders.add(provider.id);
      painted += 1;
    }
  };

  void (async () => {
    await paintProviderRows();
    await paintCategoryRows();
    if (rowCount === 0) rowsBand.remove();
  })();

  return root;
}

/**
 * Provider titles are raw catalogue strings ("[Woodman Casting] - Marisa
 * Leone casting! in 4K 🤓"). The billboard needs the title, not the noise.
 */
function billboardTitle(raw: string): string {
  return raw
    .replace(/^\s*\[[^\]]*\]\s*[-–—:]?\s*/, "")
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/\s{2,}/g, " ")
    .replace(/[\s!·–—-]+$/, "")
    .trim();
}

function formatDurationShort(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

export function providerView(context: AppContext, providerId: string): HTMLElement {
  const provider = context.providers.find((entry) => entry.id === providerId);
  const root = el("div", { class: "view view--provider" });
  let page = 1;
  // The sort a reader picked for this provider is theirs to keep.
  const sortKey = `aiox:sort:${providerId}`;
  const rememberedSort = (() => {
    try {
      const value = localStorage.getItem(sortKey);
      return value === "popular" || value === "top" || value === "new" ? value : "new";
    } catch {
      return "new";
    }
  })();
  let sort: string = rememberedSort;

  const grid = el("ul", { class: "grid grid--dense" }, ...Array.from(skeletonGrid(12, "Loading this provider").children));
  // The next page arrives when the reader reaches the end of this one. The
  // button stays for keyboards and for anyone who would rather ask.
  const more = el("button", { class: "btn btn-secondary", attrs: { type: "button" }, text: "Load more titles" });
  const moreStatus = el("p", { class: "view-foot-status", attrs: { role: "status", "aria-live": "polite" } });
  const sentinel = el("div", { class: "load-sentinel", attrs: { "aria-hidden": "true" } });
  const moreRow = el("div", { class: "view-foot" }, more, moreStatus, sentinel);

  let loading = false;
  let blankPages = 0;
  const seen = new Set<string>();
  const seenTitles = new Set<string>();
  const seenArt = new Set<string>();

  const load = async (reset: boolean) => {
    if (loading) return;
    loading = true;
    if (reset) {
      page = 1;
      blankPages = 0;
      seen.clear();
      seenTitles.clear();
      seenArt.clear();
      grid.replaceChildren(...skeletonGrid(12, "Loading this provider").children);
      moreRow.hidden = true;
    }
    more.disabled = true;
    more.dataset.loading = "true";
    moreStatus.textContent = reset ? "" : "Loading more titles…";
    try {
      const items = await fetchListing(providerId, { page, sort, limit: 36 });
      if (reset) grid.replaceChildren();
      if (items.length === 0) {
        if (reset) {
          grid.replaceChildren();
          grid.append(
            emptyState({
              title: "This provider returned no titles.",
              body: "The site may be between pages, or it may have nothing new to list.",
              action: { label: "Try another provider", onOpen: () => context.navigate("#/providers") },
            }),
          );
        }
        moreRow.hidden = true;
        return;
      }
      // A provider that ignores the page parameter would otherwise refill the
      // same titles forever; two identical pages in a row end the run. A site
      // that lists the same title three times is deduplicated on the way in.
      const fresh: typeof items = [];
      for (const item of items) {
        if (seen.has(item.id)) continue;
        const title = (item.title ?? "").replace(/\s+/g, " ").trim().toLowerCase();
        const art = item.image ?? item.thumbnail ?? "";
        if (title && seenTitles.has(title)) continue;
        if (art && seenArt.has(art)) continue;
        seen.add(item.id);
        if (title) seenTitles.add(title);
        if (art) seenArt.add(art);
        fresh.push(item);
      }
      for (const item of fresh) {
        grid.append(
          posterCard(item, {
            providerLabel: provider?.name,
            onOpen: (video) => openDetail(context, video),
          }),
        );
      }
      blankPages = fresh.length === 0 ? blankPages + 1 : 0;
      // Signed proxy URLs carry a fresh signature on every response, so the
      // comparison is made on the upstream target inside the URL.
      const upstream = (value: string | undefined): string => {
        if (!value) return "";
        const match = value.match(/[?&]u=([^&]+)/);
        if (match) {
          try {
            return decodeURIComponent(match[1]);
          } catch {
            return match[1];
          }
        }
        return value.split("?")[0];
      };
      const signature = fresh
        .map((item) => `${(item.title ?? "").trim().toLowerCase()}|${upstream(item.image ?? item.thumbnail)}`)
        .join("\n");
      if (sort === "new" && page === 1) {
        freshestPage = signature;
        sortNote.textContent = "";
      } else if (sort !== "new" && page === 1) {
        // Many engines accept any sort value and quietly serve their newest
        // page — some with fresh ids, so the comparison is on content.
        const same = signature !== "" && signature === freshestPage;
        sortNote.textContent = same
          ? `${provider?.name ?? "This provider"} returns the same page for every sort.`
          : "";
      }
      playQueue = reset ? [...fresh] : [...playQueue, ...fresh];
      page += 1;
      // A site may cap its page at 12 or 32 whatever limit is asked for, so a
      // short page is not the end of the list. Only an empty page is.
      moreRow.hidden = items.length === 0 || blankPages > 0;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load this provider";
      // Sites without a next page answer 404; that is the end of the list, not
      // a failure worth a red card under the last row.
      if (!reset && page > 1) {
        if (/\b404\b/.test(message)) {
          moreRow.hidden = true;
        } else {
          // A later page that failed is not a broken page: the rows already on
          // screen stay, and the footer offers the retry.
          moreStatus.textContent = "Could not load more titles. Try again.";
        }
        return;
      }
      // The skeleton grid is a promise that something is coming; on failure it
      // has to go, or the page shows forty placeholders and an error at once.
      if (reset) grid.replaceChildren();
      grid.append(errorState(message, () => void load(true)));
    } finally {
      loading = false;
      more.disabled = false;
      moreStatus.textContent = "";
      delete more.dataset.loading;
    }
  };

  more.addEventListener("click", () => void load(false));
  if ("IntersectionObserver" in window) {
    const watcher = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (!moreRow.hidden && !loading) void load(false);
      },
      { rootMargin: "800px 0px" },
    );
    watcher.observe(sentinel);
  }

  const sortNote = el("p", { class: "section-note", attrs: { role: "status", "aria-live": "polite" } });
  let freshestPage = "";

  const sortLabels: Record<string, string> = { new: "Newest", popular: "Most viewed", top: "Top rated" };
  const sortButtons = ["new", "popular", "top"].map((key) =>
    el("button", {
      class: "tag",
      attrs: { type: "button", "aria-pressed": String(key === sort) },
      text: sortLabels[key],
      on: {
        click: (event) => {
          sort = key;
          try {
            localStorage.setItem(sortKey, key);
          } catch {
            /* a private window is still allowed to sort */
          }
          for (const button of sortButtons) button.setAttribute("aria-pressed", String(button === event.currentTarget));
          void load(true);
        },
      },
    }),
  );

  root.append(
    el(
      "header",
      { class: "page-head container provider-head" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title provider-title", text: provider?.name ?? providerId }),
        el("p", {
          class: "page-sub",
          text: provider
            ? `${categoryTitle(provider.category)}, ${provider.siteUrl.replace(/^https?:\/\//, "")}`
            : "Provider not found in the catalogue.",
        }),
      ),
      el(
        "div",
        { class: "provider-sort" },
        el(
        "div",
        { class: "provider-sort-row" },
        el(
          "a",
          {
            class: "ghost-link provider-source",
            attrs: {
              href: provider?.siteUrl ?? "#",
              target: "_blank",
              rel: "noopener noreferrer",
            },
            text: "Open on the site",
          },
        ),
        el("div", { class: "tag-row", attrs: { role: "group", "aria-label": "Sort titles" } }, ...sortButtons),
      ),
        sortNote,
      ),
    ),
    el("div", { class: "container provider-body" }, providerSearchView(context, providerId), grid, moreRow),
  );

  void load(true);
  return root;
}

interface CoverageEntry {
  local: boolean;
  deployed: boolean;
  message: string;
}

async function loadCoverage(): Promise<Record<string, CoverageEntry>> {
  try {
    const response = await fetch("/coverage.json", { cache: "no-store" });
    if (!response.ok) return {};
    return (await response.json()) as Record<string, CoverageEntry>;
  } catch {
    return {};
  }
}

function coverageState(provider: ProviderSummary, coverage: CoverageEntry | undefined): string {
  if (coverage) {
    if (coverage.deployed) return "Verified against the worker";
    if (coverage.local) return "Blocked from the worker";
    return "Not verified";
  }
  if (provider.state === "auth") return "Account required";
  if (provider.state === "offline") return "Offline";
  return provider.siteUrl.replace(/^https?:\/\//, "");
}

export function providersView(context: AppContext): HTMLElement {
  const root = el("div", { class: "view" });
  const search = el("input", {
    class: "input",
    attrs: { type: "search", placeholder: "Filter providers", "aria-label": "Filter providers", autocomplete: "off" },
  });
  const list = el("div");
  let coverage: Record<string, CoverageEntry> = {};
  let filter = "";

  const paint = (): void => {
    list.replaceChildren();
    const query = filter.trim().toLowerCase();
    const categories = [...new Set(context.providers.map((provider) => provider.category))];
    let shown = 0;
    for (const category of categories) {
      const matches = context.providers.filter(
        (provider) =>
          provider.category === category &&
          (!query || provider.name.toLowerCase().includes(query) || provider.id.includes(query)),
      );
      if (matches.length === 0) continue;
      shown += matches.length;
      const rows = el("ul", { class: "index-list" });
      for (const provider of matches) {
        rows.append(
          indexRow({
            title: provider.name,
            state: coverageState(provider, coverage[provider.id]),
            leading: el("span", { class: "index-logo" }, logoImage(provider.logo, provider.name)),
            href: `#/provider/${provider.id}`,
            onOpen: () => context.navigate(`#/provider/${provider.id}`),
          }),
        );
      }
      list.append(
        section(categoryTitle(category), rows, {
          meta: `${matches.length} ${matches.length === 1 ? "provider" : "providers"}`,
        }),
      );
    }
    if (shown === 0) {
      list.append(
        emptyState({
          title: "No provider matches that filter.",
          body: "Clear the filter to read the whole index.",
          action: { label: "Clear filter", onOpen: () => { search.value = ""; filter = ""; paint(); } },
        }),
      );
    }
  };

  search.addEventListener("input", () => {
    filter = search.value;
    paint();
  });

  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Providers" }),
        el("p", {
          class: "page-sub",
          text: `${context.providers.length} sources in ${new Set(context.providers.map((entry) => entry.category)).size} categories`,
        }),
      ),
      el("div", { class: "field field--filter" }, el("span", { class: "field-icon", html: icons.search }), search),
    ),
    el("div", { class: "container" }, list),
  );
  paint();
  void loadCoverage().then((data) => {
    coverage = data;
    paint();
  });
  return root;
}

/** Frame list a provider may attach to a detail payload; timestamps optional. */
interface StillRef {
  url?: string;
  image?: string;
  thumbnail?: string;
  at?: number;
  time?: number;
  timestamp?: number;
}

export function detailView(context: AppContext, providerId: string, videoId: string): HTMLElement {
  const root = el("div", { class: "view view--watch" });

  // The still is the page's band, not a panel in a two-column collage: one
  // composition reads top to bottom at every width.
  const hero = el("section", { class: "watch-hero", attrs: { "aria-label": "Title artwork" } });
  const heroArt = el("div", { class: "watch-hero-art" });
  const heroTop = el("div", { class: "container watch-hero-top" });
  const heroCopy = el("div", { class: "watch-hero-copy" });
  const heroInner = el("div", { class: "container watch-hero-inner" });
  heroInner.append(heroArt, heroCopy);
  hero.append(heroTop, heroInner);

  const back = el("button", {
    class: "watch-back",
    attrs: { type: "button", "aria-label": "Back" },
    html: `${icons.back}<span class="watch-back-label">Back</span>`,
    on: { click: () => history.back() },
  });
  heroTop.append(back);

  heroCopy.append(
    el(
      "div",
      { class: "watch-hero-skeleton" },
      el("span", { class: "skeleton skeleton--line watch-skeleton-title" }),
      el("span", { class: "skeleton skeleton--line", attrs: { style: "width: 38%" } }),
    ),
  );

  const body = el("div", { class: "container watch-body" });
  const after = el("div", { class: "container watch-after" });
  const bar = el("div", { class: "watch-bar" });
  root.append(hero, body, bar, after);

  void (async () => {
    try {
      const detail: VideoDetail = await fetchDetail(providerId, videoId);
      document.title = `${detail.title.trim()} · AIOX`;

      const provider = context.providers.find((entry) => entry.id === providerId);
      const providerLabel = provider?.name ?? providerName(context, providerId);
      const category = provider ? categoryTitle(provider.category) : "";
      const title = billboardTitle(detail.title) || detail.title.trim();
      const artSource = detail.image ?? detail.thumbnail;

      if (artSource) {
        const image = el("img", {
          attrs: {
            src: await resolveImageUrl(artSource),
            alt: "",
            decoding: "async",
            fetchpriority: "high",
          },
        });
        image.addEventListener("load", () => {
          // The artwork sits beside the copy, never behind it, so its own shape
          // decides the column: a still is wide and short, a poster is tall.
          hero.dataset.ratio = image.naturalHeight > image.naturalWidth * 1.02 ? "portrait" : "wide";
        });
        image.addEventListener(
          "error",
          () => {
            image.remove();
            heroArt.dataset.empty = "true";
            hero.dataset.ratio = "empty";
          },
          { once: true },
        );
        heroArt.append(image);
      } else {
        heroArt.dataset.empty = "true";
        hero.dataset.ratio = "empty";
      }

      if (getState().settings.blurArtwork) {
        hero.dataset.revealed = "false";
        const reveal = el("button", {
          class: "watch-reveal",
          attrs: { type: "button", "aria-pressed": "false" },
          text: "Show artwork",
        });
        reveal.addEventListener("click", () => {
          const shown = hero.dataset.revealed !== "true";
          hero.dataset.revealed = String(shown);
          reveal.setAttribute("aria-pressed", String(shown));
          reveal.textContent = shown ? "Hide artwork" : "Show artwork";
        });
        heroTop.append(reveal);
      }

      // One Play control on the page: the action row. The player brings its own.
      const play = el("button", {
        class: "btn btn-primary",
        attrs: { type: "button" },
        html: `${icons.play}<span>Play</span>`,
      });
      play.addEventListener("click", () => openVideo(context, detail));

      // One meta line, said once: provider, category, length, views, date.
      const metaLine = [providerLabel, category, detail.duration, detail.views, detail.date]
        .filter((value): value is string => Boolean(value))
        .join(" · ");
      const heroChildren: Node[] = [el("h1", { class: "watch-title", text: title })];
      if (metaLine) heroChildren.push(el("p", { class: "watch-meta-line", text: metaLine }));
      heroCopy.replaceChildren(...heroChildren);

      const actions = el("div", { class: "watch-actions" }, play, favoriteButton(detail));

      const add = el("button", {
        class: "btn btn-ghost",
        attrs: { type: "button" },
        html: `${icons.plus}<span>Add to playlist</span>`,
      });
      add.addEventListener("click", () => {
        const state = getState();
        const target = state.playlists[0]?.id ?? createPlaylist("Watch later");
        addToPlaylist(target, toPlaybackRecord(detail));
        showToast("Added to playlist");
      });

      const copy = el("button", {
        class: "btn btn-ghost",
        attrs: { type: "button" },
        html: "<span>Copy link</span>",
      });
      copy.addEventListener("click", () => {
        void (async () => {
          const label = copy.querySelector("span");
          try {
            if (!navigator.clipboard) throw new Error("Clipboard unavailable");
            await navigator.clipboard.writeText(window.location.href);
            if (label) label.textContent = "Link copied";
            showToast("Link copied");
            window.setTimeout(() => {
              if (label && copy.isConnected) label.textContent = "Copy link";
            }, 2000);
          } catch {
            showAlert("This browser will not copy the link");
          }
        })();
      });
      actions.append(add, copy);

      const descSlot = el("div", { class: "watch-desc-slot" });
      const description = detail.description?.trim();
      // A description that only repeats the title is not a description.
      const repeatsTitle = description?.toLowerCase() === detail.title.trim().toLowerCase();
      if (description && !repeatsTitle) {
        const desc = el("p", { class: "watch-desc", text: description });
        const descWrap = el("div", { class: "watch-desc-wrap" }, desc);
        const more = el("button", {
          class: "ghost-link watch-more",
          attrs: { type: "button", "aria-expanded": "false" },
          html: `${icons.chevronDown}<span>More</span>`,
        });
        more.hidden = true;
        more.addEventListener("click", () => {
          const expanded = descWrap.dataset.expanded !== "true";
          descWrap.dataset.expanded = String(expanded);
          more.setAttribute("aria-expanded", String(expanded));
          const label = more.querySelector("span");
          if (label) label.textContent = expanded ? "Less" : "More";
        });
        descSlot.append(descWrap, more);

        // Measure unclamped, then put the clamp back. More exists only when the
        // text really overflows, so the face has to be loaded and the column at
        // its final width before the answer is trusted.
        let measureFrame = 0;
        const measure = () => {
          measureFrame = 0;
          if (!desc.isConnected || descWrap.dataset.expanded === "true") return;
          const clamped = desc.clientHeight;
          desc.style.display = "block";
          desc.style.setProperty("-webkit-line-clamp", "unset");
          const full = desc.clientHeight;
          desc.style.removeProperty("display");
          desc.style.removeProperty("-webkit-line-clamp");
          more.hidden = full <= clamped + 2;
        };
        const scheduleMeasure = () => {
          if (measureFrame) window.cancelAnimationFrame(measureFrame);
          measureFrame = window.requestAnimationFrame(measure);
        };
        scheduleMeasure();
        document.fonts.addEventListener("loadingdone", scheduleMeasure);
        window.addEventListener("resize", scheduleMeasure);
        window.addEventListener(
          "hashchange",
          () => {
            document.fonts.removeEventListener("loadingdone", scheduleMeasure);
            window.removeEventListener("resize", scheduleMeasure);
            window.cancelAnimationFrame(measureFrame);
          },
          { once: true },
        );
      }

      const stillsSlot = el("div", { class: "watch-stills-slot" });
      const chipsSlot = el("div", { class: "watch-chips-slot" });

      // A still is a timestamped frame of this title, taken from its own payload.
      // Anything else (a shared gallery, a neighbour in the listing) stays off
      // the page, so it can never show a second, different picture.
      const frames: { source: string; at: number }[] = [];
      const seenFrames = new Set<string>();
      for (const still of (detail as VideoDetail & { stills?: StillRef[] }).stills ?? []) {
        const source = still.url ?? still.image ?? still.thumbnail;
        const seconds = still.at ?? still.time ?? still.timestamp;
        if (!source || source === artSource || seenFrames.has(source)) continue;
        if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) continue;
        seenFrames.add(source);
        frames.push({ source, at: seconds });
        if (frames.length === 4) break;
      }
      if (frames.length > 0) {
        const sources = await Promise.all(frames.map((frame) => resolveImageUrl(frame.source)));
        const strip = el("div", {
          class: "watch-stills",
          attrs: { role: "group", "aria-label": "Frames from this title" },
        });
        frames.forEach((frame, index) => {
          const cell = el("div", { class: "watch-still" });
          const image = el("img", {
            attrs: { src: sources[index], alt: "", loading: "lazy", decoding: "async" },
          });
          image.addEventListener("error", () => cell.remove(), { once: true });
          cell.append(
            image,
            el("span", { class: "watch-still-time", text: formatClock(frame.at) }),
          );
          strip.append(cell);
        });
        stillsSlot.append(strip);
      }

      body.replaceChildren(actions, descSlot, stillsSlot, chipsSlot);

      const models = (detail.models ?? []).filter((model) => model.name);
      const tags = (detail.tags ?? []).filter(Boolean);
      const chip = (label: string, kind: "model" | "tag"): HTMLButtonElement => {
        const button = el("button", {
          class: kind === "model" ? "watch-chip watch-chip--model" : "watch-chip",
          attrs: { type: "button" },
          text: label,
        });
        button.addEventListener("click", () =>
          context.navigate(`#/search?q=${encodeURIComponent(label)}`),
        );
        return button;
      };
      if (models.length > 0) {
        const row = el(
          "div",
          { class: "watch-chips" },
          el("span", { class: "watch-chips-label", text: "Cast" }),
        );
        for (const model of models) row.append(chip(model.name, "model"));
        chipsSlot.append(row);
      }
      if (tags.length > 0) {
        const row = el(
          "div",
          { class: "watch-chips" },
          el("span", { class: "watch-chips-label", text: "Tags" }),
        );
        for (const tag of tags) row.append(chip(tag, "tag"));
        chipsSlot.append(row);
      }

      const providerRailSlot = el("div");
      const networkRailSlot = el("div");
      after.append(providerRailSlot, networkRailSlot);

      // The provider rail shares the listing fetch with nothing else: only the
      // same provider's other titles, never the page's artwork.
      void (async () => {
        let listing: VideoSummary[] = [];
        try {
          listing = await fetchListing(providerId, { sort: "popular", limit: 18 });
        } catch {
          /* rails are optional */
        }
        const others = listing.filter((item) => item.id !== detail.id);

        if (others.length > 0) {
          providerRailSlot.append(
            rail({
              title: `More from ${providerLabel}`,
              meta: `${others.length} titles`,
              items: others.slice(0, 18),
              providerLabel,
              onOpen: (video) => openDetail(context, video),
              viewAll: {
                onOpen: () => context.navigate(`#/provider/${encodeURIComponent(providerId)}`),
              },
            }),
          );
        }
      })();

      // Similar across the network: every other provider in the same category,
      // asked for this title's cast first and its own words second, so a cast
      // name the network does not index still leaves the rail standing.
      void (async () => {
        if (!provider) return;
        const siblings = context.providers
          .filter(
            (entry) =>
              entry.category === provider.category &&
              entry.id !== providerId &&
              entry.capabilities.list,
          )
          .sort(
            (a, b) =>
              Number(context.preferred.includes(b.id)) - Number(context.preferred.includes(a.id)),
          )
          .slice(0, 12);
        if (siblings.length === 0) return;

        const providerNames: Record<string, string> = {};
        for (const entry of context.providers) providerNames[entry.id] = entry.name;

        const collect = async (query: string): Promise<VideoSummary[]> => {
          const groups = await searchAll(query, siblings.map((entry) => entry.id), 4000);
          const items: VideoSummary[] = [];
          const found = new Set<string>();
          for (const group of groups) {
            for (const item of group.items ?? []) {
              const key = `${item.provider}:${item.id}`;
              if (item.provider === providerId && item.id === detail.id) continue;
              if (found.has(key)) continue;
              found.add(key);
              items.push(item);
            }
          }
          return items;
        };

        const castQuery = [models[0]?.name, tags[0]].filter(Boolean).join(" ");
        const titleQuery = title.split(/\s+/).slice(0, 3).join(" ");
        const queries = [...new Set([castQuery, titleQuery].filter((query) => query.length > 0))];

        for (const query of queries) {
          try {
            const items = await collect(query);
            if (items.length === 0) continue;
            networkRailSlot.append(
              rail({
                title: "Similar across the network",
                meta: category,
                items: items.slice(0, 18),
                providerNames,
                onOpen: (video) => openDetail(context, video),
              }),
            );
            return;
          } catch {
            /* try the next query */
          }
        }
      })();

      // Sticky action bar: slides in once the inline actions leave the screen.
      const barFavourite = favoriteButton(detail);
      barFavourite.classList.add("btn-sm");
      bar.append(
        el(
          "div",
          { class: "container watch-bar-inner" },
          el("span", { class: "watch-bar-title", text: title }),
          el("div", { class: "watch-bar-actions" }, barFavourite),
        ),
      );

      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            bar.dataset.open = String(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          },
          { threshold: 0 },
        );
        observer.observe(actions);
        window.addEventListener("hashchange", () => observer.disconnect(), { once: true });
      }
    } catch (error) {
      heroCopy.replaceChildren(el("h1", { class: "watch-title", text: "Unavailable" }));
      body.replaceChildren(
        errorState(error instanceof Error ? error.message : "This title could not be loaded.", () =>
          context.navigate("#/watch/" + encodeURIComponent(providerId) + "/" + encodeURIComponent(videoId)),
        ),
      );
    }
  })();

  return root;
}

export function libraryView(context: AppContext): HTMLElement {
  const root = el("div", { class: "view" });
  const tabs = el("div", { class: "tag-row", attrs: { role: "group", "aria-label": "Library sections" } });
  const body = el("div", { class: "container" });

  const libraryRow = (entry: { videoId: string; title: string; thumbnail?: string; provider: string }, state: string): HTMLElement =>
    indexRow({
      title: displayTitle(entry.title),
      state,
      leading: (() => {
        const frame = el("span", { class: "index-thumb" });
        if (entry.thumbnail) {
          frame.append(
            el("img", {
              attrs: { src: entry.thumbnail, alt: "", loading: "lazy", decoding: "async", referrerpolicy: "no-referrer" },
            }),
          );
        }
        return frame;
      })(),
      href: `#/watch/${encodeURIComponent(entry.provider)}/${encodeURIComponent(entry.videoId)}`,
      onOpen: () => openDetail(context, { id: entry.videoId, title: entry.title, thumbnail: entry.thumbnail, provider: entry.provider }),
    });

  const renderFavorites = () => {
    body.replaceChildren();
    const items = favoriteList();
    if (items.length === 0) {
      body.append(
        emptyState({
          title: "No favourites saved yet.",
          body: "Open a title and press Favourite to keep it here.",
          action: { label: "Browse providers", onOpen: () => context.navigate("#/providers") },
        }),
      );
      return;
    }
    const list = el("ul", { class: "index-list" });
    for (const entry of items) list.append(libraryRow(entry, `Saved from ${providerName(context, entry.provider)}`));
    body.append(list);
  };

  const renderHistory = () => {
    body.replaceChildren();
    const items = recentHistory();
    if (items.length === 0) {
      body.append(
        emptyState({
          title: "Nothing watched yet.",
          body: "Playback is remembered here, so you can resume where you stopped.",
          action: { label: "Find something to watch", onOpen: () => context.navigate("#/search") },
        }),
      );
      return;
    }
    const list = el("ul", { class: "index-list" });
    for (const entry of items) {
      const progress = entry.duration > 0 ? Math.min(100, Math.round((entry.position / entry.duration) * 100)) : 0;
      const state = progress > 0 && progress < 97 ? `Watched to ${progress}%` : "Watched to the end";
      list.append(libraryRow(entry, `${providerName(context, entry.provider)}, ${state}`));
    }
    body.append(list);
  };

  const renderPlaylists = () => {
    body.replaceChildren();
    const state = getState();
    if (state.playlists.length === 0) {
      body.append(
        emptyState({
          title: "No playlists yet.",
          body: "Make one from Settings, then add titles from any detail page.",
          action: { label: "Open settings", onOpen: () => context.navigate("#/settings") },
        }),
      );
      return;
    }
    for (const playlist of state.playlists) {
      const list = el("ul", { class: "index-list" });
      for (const entry of playlist.items) list.append(libraryRow(entry, `From ${providerName(context, entry.provider)}`));
      body.append(
        section(playlist.name, playlist.items.length > 0 ? list : emptyState({ title: "This playlist is empty." }), {
          meta: `${playlist.items.length} ${playlist.items.length === 1 ? "title" : "titles"}`,
        }),
      );
    }
  };

  const sections: { label: string; render: () => void }[] = [
    { label: "Favourites", render: renderFavorites },
    { label: "History", render: renderHistory },
    { label: "Playlists", render: renderPlaylists },
  ];

  sections.forEach((entry, index) => {
    const button = el("button", {
      class: "tag",
      attrs: { type: "button", "aria-pressed": String(index === 0) },
      text: entry.label,
      on: {
        click: () => {
          for (const other of tabs.children) other.setAttribute("aria-pressed", "false");
          button.setAttribute("aria-pressed", "true");
          entry.render();
        },
      },
    });
    tabs.append(button);
  });

  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Library" }),
        el("p", { class: "page-sub", text: "Favourites, history and playlists kept in this browser." }),
      ),
      tabs,
    ),
    body,
  );
  renderFavorites();
  return root;
}

export function gateView(): HTMLElement {
  const gate = el("div", {
    class: "gate",
    attrs: { role: "dialog", "aria-modal": "true", "aria-label": "Age confirmation" },
  });
  const card = el("div", { class: "gate-card" });
  const enter = el("button", { class: "btn btn-primary", text: "I am 18 or older, enter", attrs: { type: "button" } });
  enter.addEventListener("click", () => {
    acceptGate();
    gate.hidden = true;
    const app = document.getElementById("app");
    if (app) app.inert = false;
  });
  const leave = el("button", { class: "btn btn-ghost", text: "Leave", attrs: { type: "button" } });
  leave.addEventListener("click", () => {
    window.location.href = "https://example.com";
  });
  card.append(
    el("h2", { class: "gate-title", text: "AIOX" }),
    el("p", {
      class: "gate-body",
      text: "This site aggregates adult content from third-party providers. By continuing you confirm that you are 18 or older and accept the provider terms.",
    }),
    el("div", { class: "gate-actions" }, enter, leave),
  );
  gate.append(card);

  // A blocking dialog owns the keyboard: focus moves into it, Tab cycles inside
  // it, and Escape leaves the same way the button does.
  // The gate is not in the document yet when it is built, so the focus call
  // waits one frame for its parent to place it.
  requestAnimationFrame(() => enter.focus({ preventScroll: true }));
  gate.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      leave.click();
      return;
    }
    if (event.key !== "Tab") return;
    const stops = [enter, leave];
    const index = stops.indexOf(document.activeElement as HTMLButtonElement);
    if (event.shiftKey && index <= 0) {
      event.preventDefault();
      leave.focus();
    } else if (!event.shiftKey && index === stops.length - 1) {
      event.preventDefault();
      enter.focus();
    }
  });
  return gate;
}

export async function loadProviders(): Promise<ProviderSummary[]> {
  return fetchProviders();
}

export { toggleFavorite };
